import { decodePathSegment, type RailRd } from "./rail-sections";

// The rail's Recently viewed RDs (NO-754): a per-user, per-browser list of RD
// ids, newest first. Ids only; names come from the live rd-options roster, so
// a renamed RD shows its new name and one the viewer can no longer reach
// drops out. Pure and relative-import only, so `tsx --test` runs it.

export const RECENT_RDS_LIMIT = 5;

export function recentRdsStorageKey(userId: string): string {
  return `alwaysai:recent-rds:${userId}`;
}

export function parseStoredRecentRds(stored: string | null): string[] {
  if (stored === null) return [];

  try {
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter((id): id is string => typeof id === "string");
  } catch {
    return [];
  }
}

export function serializeRecentRds(ids: readonly string[]): string {
  return JSON.stringify(ids);
}

/** Moves `id` to the front, without duplicates, keeping at most five. */
export function pushRecentRd(ids: readonly string[], id: string): string[] {
  return [id, ...ids.filter((existing) => existing !== id)].slice(0, RECENT_RDS_LIMIT);
}

/**
 * The order the rail shows while the page stays loaded: an RD already listed
 * keeps its place (so clicking one doesn't reshuffle the list under the
 * pointer), and a newly visited RD goes on top. The stored order still moves
 * on every visit (pushRecentRd), so the next page load shows it.
 */
export function showRecentRd(shown: readonly string[], id: string): readonly string[] {
  return shown.includes(id) ? shown : pushRecentRd(shown, id);
}

/** The stored ids the viewer still reaches, in stored order, with their names. */
export function resolveRecentRds(
  ids: readonly string[],
  options: readonly RailRd[]
): RailRd[] {
  const byId = new Map(options.map((rd) => [rd.id, rd]));

  return ids.flatMap((id) => {
    const rd = byId.get(id);

    return rd ? [rd] : [];
  });
}

/**
 * The RD a workspace path opens, as written in the URL (slug or id), or null.
 * Session detail is excluded on purpose: a session link opened from a
 * notification should not reorder the list.
 */
export function workspaceRdSegment(pathname: string): string | null {
  const match = /^\/rds\/([^/]+)(\/[^/]+)?/.exec(pathname);
  if (!match || match[2] === "/sessions") return null;

  return decodePathSegment(match[1]);
}

/** The roster entry a slug-or-id URL segment names, if the viewer reaches it. */
export function findRdBySegment(
  segment: string,
  options: readonly RailRd[]
): RailRd | undefined {
  return options.find((rd) => rd.id === segment || rd.slug === segment);
}
